import { BehaviorSubject, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { IdentifiedPaymentOrder } from '@backbase/payments-common-ang';
import { GetTransfintbanQueryRequestParams } from 'payment-api';
import { PaymentType } from '../enums/payment-type';
import { PRODUCT_KIND_NAME, PRODUCT_KIND } from '../enums/product-kind-name';
import { PaymentModeOptions } from '../enums/payment-mode-options';
import { CataloguesHttpService } from 'banesco-catalogues-api';

interface BillerResponseCustomInfo {
  Value: string;
  Type: string;
}

@Injectable({
  providedIn: 'root',
})
export class TransfersService {
  public form: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public to: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public showPayments$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public closeTransfer$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public detectChanges$ = new Subject();
  public clearAccountSelector$ = new Subject();
  public paymentLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public payment$: BehaviorSubject<IdentifiedPaymentOrder | null> = new BehaviorSubject<IdentifiedPaymentOrder | null>(
    null,
  );
  public paymentMode$: BehaviorSubject<PaymentModeOptions> = new BehaviorSubject<PaymentModeOptions>(
    PaymentModeOptions.NEW,
  );

  constructor(private decimalPipe: DecimalPipe) {}

  getDirectTransfersValidateParams(
    paymentFormValues: any,
    amount: number,
    currencyCode: string,
  ): GetTransfintbanQueryRequestParams {
    let bban = '';
    let paymentType =
      paymentFormValues?.selectedBeneficiary?.product?.productKind?.externalKindId === PRODUCT_KIND.KIND5
        ? PaymentType.BANESCO_LOAN_TRANSFER
        : PaymentType.BANESCO_INTERNAL;

    let creditCardPayment;
    if (paymentFormValues?.selectedBeneficiary?.product?.productKind?.externalKindId === PRODUCT_KIND.KIND4) {
      bban = paymentFormValues?.selectedBeneficiary.additions.mainCardNumber;
      paymentType = PaymentType.BANESCO_TDC_TRANSFER;

      creditCardPayment =
        paymentFormValues?.selectedBeneficiary?.additions?.ownAccount === 'true'
          ? { cardOwner: 'SAME_USER' }
          : { cardOwner: 'OTHER_USER' };
    } else {
      bban = paymentFormValues?.selectedBeneficiary?.accounts?.[0]
        ? paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.accountNumber
        : paymentFormValues.selectedBeneficiary.IBAN || paymentFormValues.selectedBeneficiary.BBAN;
    }

    return {
      paymentorderauxservice: {
        originatorAccount: {
          arrangementId: paymentFormValues.initiator.id,
          identification: {
            identification: paymentFormValues.initiator.accountNumber,
            schemeName: 'BBAN',
          },
        },
        paymentType,
        requestedExecutionDate: new Date().toISOString().slice(0, 10),
        transferTransactionInformation: {
          counterpartyAccount: {
            arrangementId: paymentFormValues.counterparty.id || paymentFormValues.selectedBeneficiary.id,
            identification: paymentFormValues?.selectedBeneficiary?.accounts?.[0]
              ? {
                  identification: bban,
                  schemeName: 'BBAN',
                  productType: paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.additions?.productType,
                }
              : {
                  identification: bban,
                  schemeName: 'BBAN',
                },
          },
          instructedAmount: { amount, currencyCode },
          counterpartyBank: { name: 'Banesco' },
        },
        paymentDescription: paymentFormValues?.remittanceInfo?.description,
        contactInfo: paymentFormValues?.counterparty?.selectedContact?.contactId
          ? {
              bankCode: paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.bankName || undefined,
              contactId: paymentFormValues?.selectedBeneficiary?.id || undefined,
            }
          : {
              bankCode: undefined,
              contactId: undefined,
            },
        deliveryChannel: 'BUSINESS',
        creditCardPayment,
      },
    };
  }

  getDirectInternationalPaymentsValidateParams(paymentFormValues: any, amount: number, currencyCode: string): any {
    const intermediaryBank = paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.additions?.requiresIntermediateBank
      ? paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.additions?.requiresIntermediateBank
      : null;

    let counterpartyintermediaryBank;
    if (intermediaryBank === 'true'){
      counterpartyintermediaryBank = {
        interName: (paymentFormValues.selectedBeneficiary.accounts[0].additions?.intermediateBankName ?? '').slice(0, 35),
        interBic: paymentFormValues.selectedBeneficiary.accounts[0].additions?.intermediateBankBic,
        interBankAddress1: (paymentFormValues.selectedBeneficiary.accounts[0].additions?.intermediateBankAddressLine1 ?? '').slice(0, 35),
        interBankAddress2: (paymentFormValues.selectedBeneficiary.accounts[0].additions?.intermediateBankTown ?? '').slice(0, 35),
        interBankAddress3: (paymentFormValues.selectedBeneficiary.accounts[0].additions?.intermediateBankCountry  ?? '').slice(0, 35),
      };
    }


    let paymentType = PaymentType.INTERNATIONAL_TRANSFER;
    if (paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.accountType === 'REGIONAL') {
      paymentType = PaymentType.REGIONAL_TRANSFER;
    }

    return {
      paymentorderauxservice: {
        originatorAccount: {
          arrangementId: paymentFormValues.initiator.id,
          identification: {
            identification: paymentFormValues.initiator.accountNumber,
            schemeName: 'BBAN',
          },
        },
        intermediaryBank,
        paymentType,
        requestedExecutionDate: new Date().toISOString().slice(0, 10),
        transferTransactionInformation: {
          instructedAmount: {
            amount: this.decimalPipe.transform(amount, '1.2-2')?.replace(/,/g, ''),
            currencyCode,
          },
          counterpartyAccount: {
            identification: {
              identification: paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.accountNumber,
              schemeName: 'BBAN',
              productType: paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.additions?.productType,
            },
          },
          counterpartyintermediaryBank,
          counterpartyBank: {
            name: (paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.bankName ?? '').slice(0, 35),
          },
        },
        contactInfo: {
          bankCode: paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.bankCode,
          contactId: null,
          fullName: paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.name,
          addr1: paymentFormValues?.selectedBeneficiary?.accountHolderAddressLine1,
          city: paymentFormValues?.selectedBeneficiary?.accountHolderTown,
          countryCode: paymentFormValues?.selectedBeneficiary?.accountHolderCountry,
          email: null,
          beneficiaryIssuedIdentValue: paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.additions?.idNumber,
        },
        deliveryChannel: 'BUSINESS',
      },
    };
  }

  getDirectStockExchangeValidateParams(paymentFormValues: any, amount: number, currencyCode: string): any {
    if (!paymentFormValues?.selectedBeneficiary?.additions?.customString1) {
      throw new Error('No Stock exchange selected');
    }

    const infoBank = JSON.parse(
      `{${paymentFormValues?.selectedBeneficiary?.additions?.customString1}}`.replace(/'/g, '"'),
    );

    const counterPartyName = paymentFormValues.selectedBeneficiary.desc.includes(
      paymentFormValues.selectedBeneficiary.id,
    )
      ? paymentFormValues.selectedBeneficiary.desc.replace(paymentFormValues.selectedBeneficiary.id)
      : paymentFormValues.selectedBeneficiary.desc;

    return {
      paymentorderauxservice: {
        originatorAccount: {
          arrangementId: paymentFormValues.initiator.id,
          identification: {
            identification: paymentFormValues.initiator.accountNumber,
            schemeName: 'BBAN',
          },
        },
        paymentType: PaymentType.BANESCO_STOCK_TRANSFER,
        requestedExecutionDate: new Date().toISOString().slice(0, 10),
        transferTransactionInformation: {
          instructedAmount: { amount, currencyCode },
          counterparty: { name: counterPartyName },
          counterpartyAccount: {
            identification: {
              identification: paymentFormValues.selectedBeneficiary.id,
              schemeName: 'BBAN',
              productType: '4',
            },
          },
          counterpartyBank: {
            name: (paymentFormValues?.selectedBeneficiary?.additions?.description ?? '').slice(0, 35),
          },
        },
        paymentDescription: paymentFormValues?.remittanceInfo?.description,
        contactInfo: {
          contactId: null,
          bankCode: paymentFormValues.selectedBeneficiary.id,
          fullName: paymentFormValues.selectedBeneficiary.desc,
          addr1: infoBank['Bank']['Bank_Address'],
          city: infoBank['Bank']['Bank_City'],
          countryCode: infoBank['Bank']['Bank_CountryCode'],
          beneficiaryIssuedIdentValue: infoBank['Bank']['Bank_identification'],
        },
        deliveryChannel: 'BUSINESS',
      },
    };
  }

  getDirectLoansValidateParams(
    paymentFormValues: any,
    amount: number,
    currencyCode: string,
  ): GetTransfintbanQueryRequestParams {
    return {
      paymentorderauxservice: {
        originatorAccount: {
          arrangementId: paymentFormValues.initiator.id,
          identification: {
            identification: paymentFormValues.initiator.accountNumber,
            schemeName: 'BBAN',
          },
        },
        paymentType: 'BNSC_BSS_LOAN_TRANSFER',
        requestedExecutionDate: '2022-05-05',
        transferTransactionInformation: {
          counterpartyAccount: {
            arrangementId: 'eb6cdecc-f984-4951-9d2b-213017dc5c99',
            identification: {
              identification: paymentFormValues.selectedBeneficiary.BBAN,
              schemeName: 'BBAN',
            },
          },
          instructedAmount: { amount, currencyCode },
          counterpartyBank: { name: 'Banesco' },
        },
        paymentDescription: paymentFormValues?.remittanceInfo?.description,
        contactInfo: {
          bankCode: undefined,
          contactId: undefined,
        },
      },
    };
  }

  getDirectLoanExecuteParams(
    accountNumber: string,
    originatorId: any,
    requestedExecutionDate: string,
    operationReferenceId: string,
    exchangeRate: string,
    amount: string,
    currencyCode: string,
    creditCardPaymentCardOwner: string,
    queryRqUID: string,
    debitAmount: string,
    paymentType: string,
    description?: string,
  ) {
    return {
      paymentOrdersPost: {
        originatorAccount: {
          identification: {
            identification: originatorId.identification,
            schemeName: 'ID',
          },
        },
        requestedExecutionDate: requestedExecutionDate,
        paymentType: paymentType,
        transferTransactionInformation: {
          instructedAmount: {
            amount: amount,
            currencyCode: currencyCode,
          },
          counterparty: {
            name: 'J. Sparrow',
          },
          counterpartyAccount: {
            identification: {
              identification: accountNumber,
              schemeName: 'BBAN',
            },
            selectedContact: {
              contactId: undefined,
              accountId: undefined,
            },
          },
        },
        additions: {
          description: description || '',
          exchangeRate: exchangeRate || '',
          operationReferenceId: operationReferenceId || '',
          creditCardPaymentCardOwner: '',
          bankCode: '',
          deliveryChannel: '',
          queryRqUID: queryRqUID,
        },
      },
    };
  }

  getDirectOtherBanksValidateParams(paymentFormValues: any, amount: number, currencyCode: string): any {
    const intermediaryBank = paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.additions?.requiresIntermediateBank
      ? paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.additions?.requiresIntermediateBank
      : null;

    const paymentType =
      paymentFormValues?.selectedBeneficiary?.product?.productKind?.externalKindId === PRODUCT_KIND.KIND4
        ? PaymentType.BANESCO_OTHER_BANKS_TDC
        : PaymentType.BANESCO_OTHER_BANKS;
    return {
      paymentorderauxservice: {
        originatorAccount: {
          arrangementId: paymentFormValues.initiator.id,
          identification: {
            identification: paymentFormValues.initiator.accountNumber,
            schemeName: 'BBAN',
          },
        },
        intermediaryBank,
        paymentType,
        requestedExecutionDate: new Date().toISOString().slice(0, 10),
        transferTransactionInformation: {
          counterpartyAccount: {
            identification: {
              identification: paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.accountNumber,
              schemeName: 'BBAN',
              productType: paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.additions?.productType,
            },
          },
          instructedAmount: { amount, currencyCode },
          counterpartyBank: {
            name: (paymentFormValues.selectedBeneficiary.accounts[0].bankName ?? '').slice(0, 35),
          },
        },
        paymentDescription: paymentFormValues?.remittanceInfo?.description,
        contactInfo: {
          bankCode: paymentFormValues.selectedBeneficiary.accounts[0].bankCode,
          contactId: null,
          fullName: paymentFormValues.selectedBeneficiary.name,
          addr1: paymentFormValues.selectedBeneficiary.addressLine1,
          city: '',
          countryCode: paymentFormValues.selectedBeneficiary.country,
          email: paymentFormValues.selectedBeneficiary.emailId,
          beneficiaryIssuedIdentValue: paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.additions?.idNumber,
        },
        deliveryChannel: 'BUSINESS',
      },
    };
  }

  getTaxesPaymentsValidateParams(paymentFormValues: any, amount: number, currencyCode: string): any {
    return {
      paymentorderauxservice: {
        contractNumber: paymentFormValues?.counterparty?.reference,
        deliveryChannel: 'BUSINESS',
        expirationDate: paymentFormValues?.remittanceInfo?.billerInfo?.BillInvoiceDt,
        originatorAccount: {
          arrangementId: paymentFormValues?.initiator?.id,
          identification: {
            identification: paymentFormValues?.initiator?.accountNumber,
            schemeName: 'BBAN',
          },
        },
        paymentDescription: paymentFormValues?.remittanceInfo?.description,
        paymentId: paymentFormValues?.remittanceInfo?.billerInfo?.AdditionalInfo?.[0]?.CustomInfo?.find(
          (ci: any) => ci.Type == 'COLLECTION ID',
        )?.Value,
        paymentNumber: paymentFormValues?.remittanceInfo?.billerInfo?.AdditionalInfo?.[0]?.CustomInfo?.find(
          (ci: any) => ci.Type == 'COLLECTION ID',
        )?.Value,
        paymentType: 'BNSC_BSS_TAXES_TRANSF',
        requestedExecutionDate: new Date().toISOString().slice(0, 10),
        serviceCode: paymentFormValues.selectedBeneficiary.id,
        transferTransactionInformation: {
          counterpartyAccount: {},
          counterpartyBank: {
            name: 'BANESCO',
          },
          instructedAmount: {
            amount: this.decimalPipe.transform(amount, '1.2-2')?.replace(/,/g, ''),
            currencyCode,
          },
        },
        validator: paymentFormValues?.remittanceInfo?.billerInfo?.AdditionalInfo?.[0]?.CustomInfo?.find(
          (ci: any) => ci.Type == 'VALIDATOR',
        )?.Value,
      },
    };
  }

  getServicePaymentParams(paymentFormValues: any, amount: number, currencyCode: string): any {
    const validator = paymentFormValues?.remittanceInfo?.billerInfo?.AdditionalInfo?.[0]?.CustomInfo?.find(
      (item: BillerResponseCustomInfo) => item.Type === 'VALIDATOR',
    )?.Value;
    const paymentId = paymentFormValues?.remittanceInfo?.billerInfo?.AdditionalInfo?.[0]?.CustomInfo?.find(
      (item: BillerResponseCustomInfo) => item.Type === 'COLLECTION ID',
    )?.Value;

    const body = {
      paymentorderauxservice: {
        originatorAccount: {
          arrangementId: paymentFormValues?.initiator?.id,
          identification: {
            identification: paymentFormValues?.initiator?.accountNumber,
            schemeName: 'BBAN',
          },
        },
        transferTransactionInformation: {
          counterpartyAccount: {},
          instructedAmount: {
            amount: this.decimalPipe.transform(amount, '1.2-2')?.replace(/,/g, ''),
            currencyCode,
          },
          counterpartyBank: {
            name: 'Service Payment.',
          },
        },
        paymentType: 'BNSC_BSS_SERVIC_TRANSF',
        paymentDescription: paymentFormValues?.remittanceInfo?.description,
        deliveryChannel: 'BUSINESS',
        contractNumber: paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.additions?.contractNumber,
        paymentId,
        paymentNumber: paymentFormValues?.selectedBeneficiary?.accounts?.[0]?.additions?.idGroup,
        serviceCode: paymentFormValues?.remittanceInfo?.billerInfo?.serviceCode,
        validator,
        requestedExecutionDate: new Date().toISOString().slice(0, 10),
        expirationDate: paymentFormValues?.remittanceInfo?.billerInfo?.BillInvoiceDt,
      },
    };
    return body;
  }

  getPaymentType(payment: any) {
    switch (payment?.initiator?.productKindName) {
      case PRODUCT_KIND_NAME.CurrentAccount:
        return '10';
      case PRODUCT_KIND_NAME.CreditCard:
        return '00';
      case PRODUCT_KIND_NAME.SavingAccount:
        return '20';
      default:
        return undefined;
    }
  }

  getServiceAdditions(payment: any) {
    const validator = payment?.remittanceInfo?.billerInfo?.AdditionalInfo?.[0]?.CustomInfo?.find(
      (item: any) => item?.Type === 'VALIDATOR',
    )?.Value;
    const paymentId = payment?.remittanceInfo?.billerInfo?.AdditionalInfo?.[0]?.CustomInfo?.find(
      (item: any) => item?.Type === 'COLLECTION ID',
    )?.Value;

    return {
      validator,
      paymentId,
      paymentType: this.getPaymentType(payment),
      serviceCode: payment?.remittanceInfo?.billerInfo?.serviceCode,
      expirationDate: payment?.remittanceInfo?.billerInfo?.BillInvoiceDt,
      contractNumber:
        payment?.selectedBeneficiary?.accounts?.[0]?.additions?.contractNumber || payment?.counterparty?.reference,
    };
  }

  getInternationalAdditions(payment: any) {
    const intermediaryBank =
      payment?.selectedBeneficiary?.accounts?.[0]?.additions?.requiresIntermediateBank === 'true'
        ? payment?.selectedBeneficiary?.accounts?.[0]?.additions?.intermediateBankBic
        : undefined;

    const [account] = payment?.selectedBeneficiary?.accounts;
    return {
      intermediaryBank,
      addr1: account.accountHolderAddressLine1 || null,
      city: account.accountHolderTown || null,
      countryCode: account.accountHolderCountry || null,
      partyFullName: payment?.selectedBeneficiary?.name || payment?.selectedBeneficiary?.desc || '',
      transferReason: payment?.remittanceInfo?.reason?.id,
    };
  }

  getTransferExecuteParams(payment: any, externalUserId: string): any[] {
    return payment?.paymentAuxResponse.map((paymentAuxResponse: any) => {
      let additions = {};
      let operationReferenceId = paymentAuxResponse?.transactionInfo?.operationReferenceId || undefined;
      let forexExchangeReference;

      // Service payment specific
      if (
        payment?.paymentType === PaymentType?.SERVICE_TRANSFERS ||
        payment?.paymentType === PaymentType.BNSC_BSS_TAXES_TRANSF
      ) {
        forexExchangeReference = paymentAuxResponse?.transactionInfo?.operationReferenceId || undefined;
        operationReferenceId = undefined;
        additions = this.getServiceAdditions(payment);
      }

      if (payment?.paymentType === PaymentType?.INTERNATIONAL_TRANSFER) {
        forexExchangeReference = paymentAuxResponse?.transactionInfo?.operationReferenceId || undefined;
        operationReferenceId = undefined;
        additions = this.getInternationalAdditions(payment);
        if (payment?.selectedBeneficiary?.accounts?.[0]?.accountType === 'REGIONAL') {
          paymentAuxResponse.paymentType = 'BNSC_BSS_REGION_TRANSF';
        }
      }

      let creditCardPayment;
      if (payment?.selectedBeneficiary?.product?.productKind?.externalKindId === PRODUCT_KIND.KIND4) {
        creditCardPayment =
          payment?.selectedBeneficiary?.additions?.ownAccount === 'true'
            ? { cardOwner: 'SAME_USER' }
            : { cardOwner: 'OTHER_USER' };
      }

      let bankInformacion: any = {
        bankAddress1: (payment?.selectedBeneficiary?.accounts?.[0]?.bankAddressLine1 ?? '').slice(0, 35),
        bankAddress2: (payment?.selectedBeneficiary?.accounts?.[0]?.bankTown ?? '').slice(0, 35),
        bankAddress3: (payment?.selectedBeneficiary?.accounts?.[0]?.bankCountry ?? '').slice(0, 35),
      };
      if (paymentAuxResponse?.transferTransactionInformation?.counterpartyintermediaryBank) {
        bankInformacion = {
          ...bankInformacion,
          bankIntName: paymentAuxResponse.transferTransactionInformation.counterpartyintermediaryBank.interName.slice(0, 35),
          intermediaryBank: paymentAuxResponse.transferTransactionInformation.counterpartyintermediaryBank.interBic,
          bankIntAddress1:
            paymentAuxResponse.transferTransactionInformation.counterpartyintermediaryBank.interBankAddress1.slice(0, 35),
          bankIntAddress2:
            paymentAuxResponse.transferTransactionInformation.counterpartyintermediaryBank.interBankAddress2.slice(0, 35),
          bankIntAddress3:
            paymentAuxResponse.transferTransactionInformation.counterpartyintermediaryBank.interBankAddress3.slice(0, 35),
        };
      }

      return {
        paymentOrdersPost: {
          originatorAccount: {
            identification: {
              identification: payment?.initiator?.id,
              schemeName: 'ID',
            },
          },
          requestedExecutionDate: paymentAuxResponse?.requestedExecutionDate,
          paymentType: paymentAuxResponse?.paymentType,
          transferTransactionInformation: {
            instructedAmount: {
              amount: this.decimalPipe
                .transform(+paymentAuxResponse?.transferTransactionInformation.instructedAmount.amount, '1.2-2')
                ?.replace(/,/g, ''),
              currencyCode: paymentAuxResponse?.transferTransactionInformation.instructedAmount.currencyCode,
            },
            counterparty: {
              name: payment?.counterparty?.name || '',
            },
            counterpartyAccount: {
              identification: {
                identification:
                  paymentAuxResponse?.transferTransactionInformation?.counterpartyAccount?.identification
                    ?.identification || '',
                schemeName: 'BBAN',
              },
            },
          },
          additions: {
            debitAccount:
              payment?.initiator?.externalKindId !== 'kind4' ? payment?.initiator?.accountNumber : undefined,
            externalUserId,
            description: payment?.remittanceInfo?.description || '',
            beneficiaryIssuedIdentValue: paymentAuxResponse?.contactInfo?.beneficiaryIssuedIdentValue,
            exchangeRate: paymentAuxResponse?.transactionInfo?.exchangeRate || undefined,
            operationReferenceId,
            forexExchangeReference,
            creditCardPaymentCardOwner: creditCardPayment?.cardOwner,
            bankCode: paymentAuxResponse?.contactInfo?.bankCode,
            commissionAmount: paymentAuxResponse?.transactionInfo?.commisionAmt,
            deliveryChannel:
              paymentAuxResponse?.deliveryChannel ||
              payment?.paymentAuxRequest?.[0]?.paymentorderauxservice?.deliveryChannel,
            queryRqUID: paymentAuxResponse?.statusInfo?.rqUID || undefined,
            fullName: payment?.selectedBeneficiary?.name || payment?.selectedBeneficiary?.desc || '',
            debitAmount: paymentAuxResponse?.transactionInfo?.debitAmount || undefined,
            paymentNumber:
              payment?.selectedBeneficiary?.accounts?.[0]?.additions?.idGroup ||
              payment?.remittanceInfo?.billerInfo?.serviceCode,
            productTypeBenf: payment?.selectedBeneficiary?.accounts?.[0]?.additions?.productType,
            email: payment?.selectedBeneficiary?.emailId || undefined,
            bankName: payment?.selectedBeneficiary?.accounts?.[0]?.bankName
              ? payment.selectedBeneficiary.accounts[0].bankName.slice(0, 35)
              : undefined,
            tax:
              payment?.paymentAuxResponse?.[0]?.transactionInfo?.taxData1 +
              payment?.paymentAuxResponse?.[0]?.transactionInfo?.taxData2,
            ...additions,
            ...bankInformacion,
          },
        },
      };
    });
  }
}
